import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  ReactNode,
} from 'react';

import queryString from 'query-string';
import cn from 'classnames';
import { IoChevronBackSharp, IoChevronForwardSharp } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { QUERY_OPTIONS } from 'hooks/Api';

export const QuickPaginationContext = createContext<{
  pages: number[];
  setPages?: React.Dispatch<React.SetStateAction<any[]>>;
  currentPage: number;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  setStageId?: React.Dispatch<React.SetStateAction<string>>;
  stageId: string;
}>({ pages: [], currentPage: 0, stageId: '' });

// eslint-disable-next-line react-refresh/only-export-components
export const useQuickPaginationContext = () =>
  useContext(QuickPaginationContext);

export function QuickPaginationWrapper({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [pages, setPages] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [stageId, setStageId] = useState('');

  const value = useMemo(
    () => ({
      pages,
      setPages,
      currentPage,
      setCurrentPage,
      stageId,
      setStageId,
    }),
    [pages, setPages, currentPage, setCurrentPage, stageId, setStageId],
  );

  return (
    <QuickPaginationContext.Provider value={value}>
      {children}
    </QuickPaginationContext.Provider>
  );
}

type QuickPaginationProps = {
  prevLabel: string;
  nextLabel?: string;
  onAction?: (newPage: number) => void;
  url?: string;
  params?: Record<string, any>;
};

function QuickPagination({
  prevLabel,
  nextLabel,
  onAction,
  url,
  params,
}: QuickPaginationProps) {
  const { pages, currentPage, setCurrentPage } = useQuickPaginationContext();
  const navigate = useNavigate();

  const handleAction = useCallback(
    (nav: number) => {
      const newPage = currentPage + nav;

      setCurrentPage?.(newPage);
      onAction?.(newPage);
    },
    [currentPage, setCurrentPage, onAction],
  );

  const handleUrlAction = useCallback(
    (nav: number) => {
      const newPage = currentPage + nav;

      setCurrentPage?.(newPage);

      navigate(
        `${url}/${newPage}?${queryString.stringify(params ?? {}, QUERY_OPTIONS)}`,
      );
    },
    [currentPage, setCurrentPage, url, navigate, params],
  );

  return (
    <div className="flex justify-between w-full mb-[22px]">
      <button
        type="button"
        onClick={() => {
          if (onAction) {
            return handleAction(-1);
          }
          return handleUrlAction(-1);
        }}
        className={cn(
          'text-xs leading-tight h-fit',
          'flex items-center gap-2 font-semibold text-secondary-base',
          'hover:text-primary-base',
          'disabled:text-disabled',
        )}
        disabled={currentPage <= 0}
      >
        <IoChevronBackSharp className="w-[14px] h-[14px]" />
        {prevLabel}
      </button>
      {nextLabel && (
        <button
          type="button"
          onClick={() => {
            if (onAction) return handleAction(1);
            return handleUrlAction(1);
          }}
          className={cn(
            'text-xs leading-tight h-fit',
            'flex items-center gap-2 font-semibold',
            'text-primary-base',
            'disabled:text-disabled',
          )}
          disabled={currentPage >= pages.length - 1}
        >
          {nextLabel}
          <IoChevronForwardSharp className="w-[14px] h-[14px]" />
        </button>
      )}
    </div>
  );
}

export default QuickPagination;
